// Display names for the withdrawal methods. The methods themselves are
// implemented in retirementPlan.js.
import { formatCurrency } from '../formatters';

export const WITHDRAWAL_STRATEGY_LABELS = {
  fixed: 'Fixed Dollar (Inflation-Adjusted)',
  percent: 'Percent of Portfolio',
  guyton_klinger: 'Guardrails (Guyton-Klinger)',
  bucket: 'Bucket Strategy',
  vpw: 'Variable Percentage (VPW)',
  rmd_based: 'RMD-Based',
  vanguard_dynamic: 'Vanguard Dynamic Spending',
};

// A Roth conversion setting is null (none), a bracket rate (convert to the top
// of that bracket each year), 'ladder', or a credit policy: 'credit' keeps
// income at the level where the health premium credit stops in the years on
// marketplace coverage and converts nothing otherwise; 'credit:0.22' does the
// same and fills the named bracket in every other year.
// A phased policy fills one bracket until pensions and Social Security start
// and another (or none) afterwards: 'phase:0.24:0.12', 'phase:0.22:'.
export const CREDIT = 'credit';
export const PHASE = 'phase';
export const creditCode = (rate = null) => (rate == null ? CREDIT : `${CREDIT}:${rate}`);
export const phaseCode = (early, late = null) => `${PHASE}:${early}:${late == null ? '' : late}`;
export function conversionParts(code) {
  const text = typeof code === 'string' ? code : '';
  const credit = text === CREDIT || text.startsWith(`${CREDIT}:`);
  const phased = text.startsWith(`${PHASE}:`);
  const number = (value) => (value !== '' && value != null && Number.isFinite(Number(value)) ? Number(value) : null);
  const [, first, second] = text.split(':');
  const rate = typeof code === 'number' ? code : credit || phased ? number(first) : null;
  // `rate` is the bracket filled (for a phased policy, before income starts); `lateRate` the one after.
  return { ladder: code === 'ladder', credit, phased, rate, lateRate: phased ? number(second) : null };
}

// How a Roth conversion setting reads.
const bracket = (rate) => `${Math.round(rate * 100)}%`;
export const conversionChoice = (code) => {
  if (code == null) return 'Nothing';
  const { ladder, credit, phased, rate, lateRate } = conversionParts(code);
  if (ladder) return 'A ladder: what the plan needs in five years';
  if (credit) return rate == null ? 'Up to the health credit limit' : `Up to the health credit limit, then top of ${bracket(rate)} bracket`;
  if (phased) return `To top of ${bracket(rate)} bracket until pensions and Social Security start, then ${lateRate == null ? 'nothing' : `the ${bracket(lateRate)} bracket`}`;
  return `To top of ${bracket(rate)} bracket`;
};
// The same, to follow "Roth conversions ..." in a sentence.
export const conversionPhrase = (code) => {
  const { ladder, credit, phased, rate, lateRate } = conversionParts(code);
  if (phased) return `to the top of the ${bracket(rate)} bracket each year until pensions and Social Security start, and ${lateRate == null ? 'nothing after that' : `to the top of the ${bracket(lateRate)} bracket after that`}`;
  if (ladder) return 'as a ladder (each year, what the plan will need five years later)';
  if (credit) return `up to the income at which the health premium credit stops, in the years on marketplace coverage${rate == null ? '' : `, and to the top of the ${bracket(rate)} bracket in every other year`}`;
  return `to the top of the ${bracket(rate)} bracket each year`;
};
// A short name for a setting, to start a sentence about it.
export const conversionName = (code) => {
  if (code == null) return 'Converting nothing';
  const { ladder, credit, phased, rate, lateRate } = conversionParts(code);
  if (phased) return `To the ${bracket(rate)} bracket, then ${lateRate == null ? 'nothing' : `the ${bracket(lateRate)} bracket`}`;
  if (ladder) return 'The ladder';
  if (credit) return rate == null ? 'To the health credit limit' : `To the health credit limit, then the ${bracket(rate)} bracket`;
  return `To the ${bracket(rate)} bracket`;
};

// A row of the early-access comparison (`plan.earlyAccess.options`). A payment
// sized to the need that hits the largest allowed, and still leaves a penalty,
// says so: "sized to the need" would read as if the need were covered.
const EARLY_OPTION_LABELS = {
  none: 'Nothing special',
  sepp: '72(t) payments sized to the need',
  ladder: 'Roth conversion ladder',
  both: '72(t) payments and a ladder',
  plan: 'Your current plan',
};
export const largestSeppPhrase = (annual) => `the largest allowed (${formatCurrency(Math.round(annual / 100) * 100)} a year)`;
export const earlyOptionLabel = (option) => {
  if (!option.seppShort) return EARLY_OPTION_LABELS[option.id];
  const largest = largestSeppPhrase(option.seppAnnual);
  if (option.id === 'sepp') return `72(t) payments: ${largest}, still short of the need`;
  if (option.id === 'both') return `72(t) payments at ${largest}, still short of the need, and a ladder`;
  return EARLY_OPTION_LABELS[option.id];
};
